import React, { useEffect, useState, useRef, useContext } from 'react';
import { Box, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Snackbar, Alert } from '@mui/material';
import BookingDetailModal from './components/BookingDetailModal';
import { motion } from "framer-motion";

const ReservationCenter = () => {
  const SkeletonBlock = ({ style = {} }) => (
    <motion.div
      initial={{ backgroundPositionX: '100%' }}
      animate={{ backgroundPositionX: '-100%' }}
      transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
      style={{
        width: '100%',
        height: '20px',
        borderRadius: '6px',
        backgroundImage: 'linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)',
        backgroundSize: '200% 100%',
        backgroundRepeat: 'no-repeat',
        ...style
      }}
    />
  );
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState('all');
  const [subTab, setSubTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [newBookings, setNewBookings] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const prevBookingsRef = useRef([]);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin';
  const allowedFeatures = isAdmin
    ? ['modifyBooking', 'cancelBooking', 'noShowBooking']
    : (user?.rolePermission?.allowedFeatures || []);
  const propertyId = user?.propertyId?.id;

  // Mark all bookings as seen when the component mounts
  useEffect(() => {
    if (!propertyId) return;
    fetch(`${process.env.REACT_APP_API_URL}/api/bookings/mark-seen?propertyId=${propertyId}`, {
      method: 'PUT'
    });
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    const today = new Date().toISOString().split('T')[0];
    fetch(`${process.env.REACT_APP_API_URL}/api/bookings/by-property?propertyId=${propertyId}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setBookings(data.bookings);
        const now = new Date();
        const viewed = JSON.parse(localStorage.getItem('viewedBookings')) || [];
        const newOnes = data.bookings
          .filter(b => {
            const updatedAt = new Date(b.updatedAt || b.createdAt);
            return (now - updatedAt) < 24 * 60 * 60 * 1000 && !viewed.includes(b._id);
          })
          .map(b => b._id);
        setNewBookings(newOnes);
        prevBookingsRef.current = data.bookings;
      });
  }, [propertyId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!propertyId) return;
      fetch(`${process.env.REACT_APP_API_URL}/api/bookings/by-property?propertyId=${propertyId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            console.log("🔁 Polling bookings...");
            const prev = prevBookingsRef.current;
            const current = data.bookings;
            const prevIds = new Set(prev.map(b => b._id));
            const currentIds = new Set(current.map(b => b._id));

            // New booking
            console.log("🆕 Checking for new booking...");
            const newBooking = current.find(b => !prevIds.has(b._id));
            if (newBooking) {
              setSnackbar({ open: true, message: `New booking: ${newBooking.referenceNumber} (${newBooking.rooms?.[0]?.roomType})`, severity: 'success' });
              setNewBookings(prev => [...new Set([...prev, newBooking._id])]);
            }

            // Modified or Cancelled (ignore new booking)
            console.log("🛠️ Checking for modified or cancelled booking...");
            const modified = current.find(b => {
              const match = prev.find(p => p._id === b._id);
              return (
                match &&
                b.updatedAt !== match.updatedAt &&
                b._id !== newBooking?._id // ensure it's not the new one
              );
            });
            if (modified) {
              console.log("✅ Modified booking detected:", modified);
              const type = modified.status === 'cancelled' ? 'cancelled' : 'modified';
              setSnackbar({ open: true, message: `Booking ${type}: ${modified.referenceNumber} (${modified.rooms?.[0]?.roomType})`, severity: 'info' });
              setNewBookings(prev => [...new Set([...prev, modified._id])]);
            }

            prevBookingsRef.current = current;
            setBookings(current);
          }
        });
    }, 30000);

    return () => clearInterval(interval);
  }, [propertyId]);

  const filterByTab = (b) => {
    const today = new Date();
    const checkIn = new Date(b.checkIn);
    const checkOut = new Date(b.checkOut);

    switch (tab) {
      case 'all': return true;
      case 'upcoming': {
        if (subTab === 'today') {
          const todayStr = today.toISOString().split('T')[0];
          const checkInStr = checkIn.toISOString().split('T')[0];
          return b.status === 'confirmed' && checkInStr === todayStr;
        }
        if (b.status !== 'confirmed') return false;
        const isUpcoming = checkIn > today;
        if (!isUpcoming) return false;

        if (subTab === 'tomorrow') {
          const tomorrow = new Date(today);
          tomorrow.setDate(today.getDate() + 1);
          return checkIn.toDateString() === tomorrow.toDateString();
        }

        if (subTab === 'week') {
          const end = new Date(today);
          end.setDate(today.getDate() + 7);
          return checkIn >= today && checkIn <= end;
        }

        if (subTab === 'nextweek') {
          const start = new Date(today);
          const end = new Date(today);
          start.setDate(today.getDate() + 7);
          end.setDate(today.getDate() + 14);
          return checkIn >= start && checkIn <= end;
        }

        if (subTab === 'month') {
          const currentMonth = today.getMonth();
          const currentYear = today.getFullYear();
          return checkIn.getMonth() === currentMonth && checkIn.getFullYear() === currentYear;
        }

        if (subTab === 'nextmonth') {
          const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
          const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
          return checkIn >= start && checkIn <= end;
        }

        if (subTab === 'thisquarter') {
          const quarter = Math.floor(today.getMonth() / 3);
          const start = new Date(today.getFullYear(), quarter * 3, 1);
          const end = new Date(today.getFullYear(), quarter * 3 + 3, 0);
          return checkIn >= start && checkIn <= end;
        }

        if (subTab === 'nextquarter') {
          const quarter = Math.floor(today.getMonth() / 3) + 1;
          const start = new Date(today.getFullYear(), quarter * 3, 1);
          const end = new Date(today.getFullYear(), quarter * 3 + 3, 0);
          return checkIn >= start && checkIn <= end;
        }

        if (subTab === 'thisyear') {
          const start = new Date(today.getFullYear(), 0, 1);
          const end = new Date(today.getFullYear(), 11, 31);
          return checkIn >= start && checkIn <= end;
        }

        if (subTab === 'nextyear') {
          const nextYear = today.getFullYear() + 1;
          const start = new Date(nextYear, 0, 1);
          const end = new Date(nextYear, 11, 31);
          return checkIn >= start && checkIn <= end;
        }

        return true;
      }
      case 'current': return checkIn <= today && checkOut >= today && b.status === 'confirmed';
      case 'past': return checkOut < today;
      case 'cancelled': return b.status === 'cancelled';
      default: return true;
    }
  };

  const getBookingTotal = (booking) => {
    const total = booking.rooms?.reduce((sum, room) => sum + (room.totalAfterTax || 0), 0);
    return total.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  return (
    <Box p={3}>
      <Tabs value={tab} onChange={(e, newVal) => setTab(newVal)} sx={{ mb: 2 }}>
        <Tab label="All" value="all" />
        <Tab label="Upcoming" value="upcoming" />
        <Tab label="Current Stay" value="current" />
        <Tab label="Past" value="past" />
        <Tab label="Cancelled" value="cancelled" />
      </Tabs>
      {tab === 'upcoming' && (
        <Tabs
          value={subTab}
          onChange={(e, val) => setSubTab(val)}
          sx={{
            mb: 2,
            minHeight: '32px',
            '& .MuiTab-root': {
              fontSize: '0.75rem',
              minHeight: '28px',
              py: 0
            }
          }}
          textColor="primary"
        >
          <Tab label="All" value="all" />
          <Tab label="Today" value="today" />
          <Tab label="Tomorrow" value="tomorrow" />
          <Tab label="This Week" value="week" />
          <Tab label="Next Week" value="nextweek" />
          <Tab label="This Month" value="month" />
          <Tab label="Next Month" value="nextmonth" />
          <Tab label="This Quarter" value="thisquarter" />
          <Tab label="Next Quarter" value="nextquarter" />
          <Tab label="This Year" value="thisyear" />
          <Tab label="Next Year" value="nextyear" />
        </Tabs>
      )}
      <Box mb={2}>
        <input
          type="text"
          placeholder="Search by guest name or reference..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ccc', width: '100%', maxWidth: '320px' }}
        />
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ref</TableCell>
              <TableCell>Guest</TableCell>
              <TableCell>Check-in</TableCell>
              <TableCell>Check-out</TableCell>
              <TableCell>Nights</TableCell>
              <TableCell>Rooms</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Total ($)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.length === 0 ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={8}>
                    <SkeletonBlock style={{ marginBottom: '10px' }} />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              (() => {
                const filtered = bookings.filter(filterByTab).filter(b => {
                  const term = searchTerm.toLowerCase();
                  return (
                    b.fullName?.toLowerCase().includes(term) ||
                    b.referenceNumber?.toLowerCase().includes(term)
                  );
                });

                if (filtered.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No bookings found.
                      </TableCell>
                    </TableRow>
                  );
                }

                const sorted = tab === 'all'
                  ? [...filtered].sort((a, b) => {
                      const aNew = newBookings.includes(a._id);
                      const bNew = newBookings.includes(b._id);
                      return bNew - aNew;
                    })
                  : filtered;

                return sorted.map(b => (
                  <TableRow
                    key={b._id}
                    onClick={() => {
                      setSelectedBooking(b);
                      const updated = newBookings.filter(id => id !== b._id);
                      setNewBookings(updated);
                      localStorage.setItem('viewedBookings', JSON.stringify([...new Set([
                        ...(JSON.parse(localStorage.getItem('viewedBookings')) || []),
                        b._id
                      ])]));
                    }}
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <TableCell>
                      {b.referenceNumber}
                      {newBookings.includes(b._id) && !(() => {
                        const now = new Date();
                        const createdAt = new Date(b.createdAt);
                        const checkIn = new Date(b.checkIn);
                        const timeDiff = now - createdAt;
                        const sixHours = 6 * 60 * 60 * 1000;
                        const isToday = checkIn.toDateString() === now.toDateString();
                        return isToday && timeDiff < sixHours;
                      })() && (
                        <span style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: '8px',
                          marginLeft: '6px'
                        }}>
                          NEW
                        </span>
                      )}
                      {(() => {
                        const now = new Date();
                        const createdAt = new Date(b.createdAt);
                        const checkIn = new Date(b.checkIn);
                        const timeDiff = now - createdAt;
                        const sixHours = 6 * 60 * 60 * 1000;
                        const isToday = checkIn.toDateString() === now.toDateString();
                        const isLastMinute = isToday && timeDiff < sixHours;
                        return isToday && !isLastMinute ? (
                          <span style={{
                            backgroundColor: '#ff9800',
                            color: 'white',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            padding: '2px 6px',
                            borderRadius: '8px',
                            marginLeft: '6px'
                          }}>
                            TODAY
                          </span>
                        ) : null;
                      })()}
                      {(() => {
                        const now = new Date();
                        const createdAt = new Date(b.createdAt);
                        const checkIn = new Date(b.checkIn);
                        const timeDiff = now - createdAt;
                        const sixHours = 6 * 60 * 60 * 1000;
                        const isToday = checkIn.toDateString() === now.toDateString();
                        // Hide LAST MINUTE if booking is not in newBookings
                        return isToday && timeDiff < sixHours && newBookings.includes(b._id) ? (
                          <span style={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            padding: '2px 6px',
                            borderRadius: '8px',
                            marginLeft: '6px'
                          }}>
                            LAST MINUTE
                          </span>
                        ) : null;
                      })()}
                    </TableCell>
                    <TableCell>{b.fullName}</TableCell>
                    <TableCell>{new Date(b.checkIn).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(b.checkOut).toLocaleDateString()}</TableCell>
                    <TableCell>{b.nights}</TableCell>
                    <TableCell>{b.numberOfRooms}</TableCell>
                    <TableCell>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          color: '#fff',
                          backgroundColor:
                            b.status === 'confirmed' ? '#4caf50' :
                            b.status === 'cancelled' ? '#f44336' :
                            b.status === 'no-show' ? '#ff9800' :
                            '#9e9e9e'
                        }}
                      >
                        {b.status}
                      </span>
                    </TableCell>
                    <TableCell>{getBookingTotal(b)}</TableCell>
                  </TableRow>
                ));
              })()
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={15000}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <BookingDetailModal
        booking={selectedBooking}
        open={Boolean(selectedBooking)}
        onClose={() => setSelectedBooking(null)}
        getBookingTotal={getBookingTotal}
        allowedFeatures={allowedFeatures}
      />
    </Box>
  );
};

export default ReservationCenter;